import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { Ban, Images, Search } from 'lucide-react';

import { apiPatch } from '../../lib/api_v2';
import db from '../../lib/db';
import { logError } from '../../lib/logger';
import { cn } from '../../lib/utils';
import { isAvCallingEnabled } from '../../lib/workspace';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import useDirectMessageBackground from '../../hooks/useDirectMessageBackground';
import useModalSections from '../../hooks/useModalSections';
import Flexicon from '../shared/Flexicon';
import GroupProfilePanel from '../shared/GroupProfilePanel';
import ProfileTag from '../shared/ProfileTag';
import SettingsBackButton from '../shared/SettingsBackButton';
import { SettingsGroup, SettingsQuickAction, SettingsRow, SettingsToggle } from '../shared/SettingsList';
import WorkspaceBackgroundPicker from '../shared/WorkspaceBackgroundPicker';
import { SETTINGS_MODAL_CLOSE_BUTTON_CLASS, SETTINGS_MODAL_CLOSE_ICON, SETTINGS_MODAL_TITLE_CLASS } from '../shared/settingsModalChrome';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalContent } from './ModalContent';
import { ModalTopBar } from './ModalTopBar';

const SECTION = {
  ROOT: 'root',
  NAME_TAG: 'name_tag',
  BACKGROUND: 'background',
};
const SECTION_TITLE = {
  [SECTION.NAME_TAG]: 'Name Tag',
  [SECTION.BACKGROUND]: 'Background Image',
};
const MODAL_SECTION_RESET_DELAY_MS = 200;

// DMs only have on/off: only_mentions (the profile enum default) counts as on.
const DM_NOTIFICATIONS_OFF = 'no_notifications';
const DM_NOTIFICATIONS_ON = 'all_notifications';

// The shared Background Image card, saving through the DM endpoint (the background
// lives on the DM's hidden workspace, which participants can't PATCH directly).
const DirectMessageBackgroundImageSettings = () => {
  const { backgroundResource, saveAttachment, savePreset } = useDirectMessageBackground();

  return (
    <div className="flex w-full justify-center pt-10 pb-2">
      <WorkspaceBackgroundPicker
        workspace={backgroundResource}
        sessionKeyScope="direct-message-preferences"
        dismissible={false}
        saveAttachment={saveAttachment}
        savePreset={savePreset}
      />
    </div>
  );
};

// Applies instantly like the group modal's feature toggles: optimistic with revert on failure.
const NotificationsToggle = () => {
  const { profile, profileId } = useRouteWorkspace();
  const profileNotificationsOn = profile?.notification_preference !== DM_NOTIFICATIONS_OFF;
  const [notificationsOn, setNotificationsOn] = useState(profileNotificationsOn);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNotificationsOn(profileNotificationsOn);
  }, [profileId, profileNotificationsOn]);

  const handleChange = useCallback(
    async (next) => {
      if (!profileId) return;

      setNotificationsOn(next);
      setIsSaving(true);

      try {
        const response = await apiPatch(`/profiles/${profileId}`, {
          profile: { notification_preference: next ? DM_NOTIFICATIONS_ON : DM_NOTIFICATIONS_OFF },
        });
        const data = await response.json();
        const updatedProfile = data?.data?.profile;

        if (!response.ok || data.ok !== true || !updatedProfile) {
          setNotificationsOn(!next);
          return;
        }

        db.profiles.put(updatedProfile).catch((cacheError) => {
          logError('DirectMessagePreferencesModal', 'Failed to cache updated profile', { error: cacheError });
        });
      } catch (saveError) {
        setNotificationsOn(!next);
        logError('DirectMessagePreferencesModal', 'Failed to save notification preference', { error: saveError });
      } finally {
        setIsSaving(false);
      }
    },
    [profileId],
  );

  return <SettingsToggle label="Notifications" checked={notificationsOn} disabled={isSaving || !profileId} onChange={handleChange} />;
};

// Unlike Notifications above, this one is shared: it lives on the conversation's hidden
// workspace, so either participant switching video chat off switches it off for both.
const VideoChatToggle = () => {
  const { workspace, directMessage, directMessageId } = useRouteWorkspace();
  const persistedVideoChatOn = isAvCallingEnabled(workspace);
  const [videoChatOn, setVideoChatOn] = useState(persistedVideoChatOn);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setVideoChatOn(persistedVideoChatOn);
  }, [directMessageId, persistedVideoChatOn]);

  const handleChange = useCallback(
    async (next) => {
      if (!directMessageId) return;

      setVideoChatOn(next);
      setIsSaving(true);

      try {
        const response = await apiPatch(`/direct_messages/${directMessageId}`, { direct_message: { av_calling_enabled: next } });
        const data = await response.json();
        const updatedDirectMessage = data?.data?.direct_message;

        if (!response.ok || data.ok !== true || !updatedDirectMessage) {
          setVideoChatOn(!next);
          return;
        }

        storeDirectMessagePayload(updatedDirectMessage).catch((cacheError) => {
          logError('DirectMessagePreferencesModal', 'Failed to cache updated direct message', { error: cacheError });
        });
      } catch (saveError) {
        setVideoChatOn(!next);
        logError('DirectMessagePreferencesModal', 'Failed to save video chat preference', { error: saveError });
      } finally {
        setIsSaving(false);
      }
    },
    [directMessageId],
  );

  if (!directMessage) return null;

  return <SettingsToggle label="Video chat" checked={videoChatOn} disabled={isSaving} onChange={handleChange} />;
};

const DirectMessagePreferencesModal = ({ open, onClose, onOpenSearch }) => {
  const { directMessage, workspaceId, profile } = useRouteWorkspace();
  const { section, isNested, goToSection, goBack } = useModalSections({
    open,
    initialSection: SECTION.ROOT,
    resetDelayMs: MODAL_SECTION_RESET_DELAY_MS,
  });
  const sectionTitle = SECTION_TITLE[section] || null;
  const modalContentRef = useRef(null);

  useEffect(() => {
    if (!modalContentRef.current) return;
    modalContentRef.current.scrollTop = 0;
  }, [section]);

  // Prefer the live profile row (avatar edits sync through the journal); the
  // snapshot on the direct message is the fallback for first paint.
  const peerProfileId = directMessage?.peer_profile?.id ? String(directMessage.peer_profile.id) : null;
  const livePeerProfile = useLiveQuery(
    () => (workspaceId && peerProfileId ? db.profiles.get([workspaceId, peerProfileId]) : null),
    [workspaceId, peerProfileId],
  );
  const peerProfile = livePeerProfile || directMessage?.peer_profile || null;
  const peerName = peerProfile?.full_name || 'Direct message';

  const nameTagValue = profile?.tag ? (
    <ProfileTag tag={profile.tag} hex={profile.tag_color} />
  ) : (
    <span className="font-mono text-[11px] font-semibold text-gray-400 dark:text-neutral-500">NONE</span>
  );

  const backgroundImageValue = directMessage?.background_image_url ? (
    <span className="size-6 rounded bg-cover bg-center" style={{ backgroundImage: `url(${directMessage.background_image_url})` }} />
  ) : (
    <span className="flex items-center gap-1 text-gray-400 dark:text-neutral-500">
      <Ban className="size-4" strokeWidth={1.5} absoluteStrokeWidth />
      None
    </span>
  );

  const handleOpenNameTag = useCallback(() => goToSection(SECTION.NAME_TAG), [goToSection]);
  const handleOpenBackground = useCallback(() => goToSection(SECTION.BACKGROUND), [goToSection]);

  return (
    <Modal
      onClose={onClose}
      className="rounded-2xl"
      closePlacement="top-left"
      showClose={{ desktop: !isNested, mobile: !isNested }}
      closeButtonClassName={SETTINGS_MODAL_CLOSE_BUTTON_CLASS}
      closeIcon={SETTINGS_MODAL_CLOSE_ICON}
    >
      {isNested && <SettingsBackButton onClick={goBack} />}
      <ModalBody className="h-full">
        {isNested && (
          <ModalTopBar className="!pb-0">
            <span className={SETTINGS_MODAL_TITLE_CLASS}>{sectionTitle}</span>
          </ModalTopBar>
        )}
        <ModalContent
          ref={modalContentRef}
          className={cn('min-h-0 md:!px-5', section === SECTION.ROOT && 'flex items-start justify-center')}
        >
          {section === SECTION.ROOT && (
            <div className="flex w-full flex-col items-center pt-3 pb-2">
              <span className="inline-flex aspect-square size-30 overflow-hidden rounded-full">
                <Flexicon resource={peerProfile} size="size-30" rounded="rounded-full" className="size-30" />
              </span>
              <h2 className="mt-3 max-w-full truncate text-center text-xl leading-6 font-semibold text-black dark:text-neutral-100">
                {peerName}
              </h2>

              <div className="mt-6.5 flex items-center gap-3">
                {/* Media is a visual stub until its destination exists */}
                <SettingsQuickAction icon={Search} label="Search" onClick={onOpenSearch} />
                <SettingsQuickAction icon={Images} label="Media" disabled />
              </div>

              <div className="mt-7 flex w-full flex-col gap-5">
                <SettingsGroup>
                  <SettingsRow label="Name Tag" value={nameTagValue} onClick={handleOpenNameTag} />
                  <SettingsRow label="Background Image" value={backgroundImageValue} onClick={handleOpenBackground} />
                  <NotificationsToggle />
                  <VideoChatToggle />
                </SettingsGroup>
              </div>
            </div>
          )}

          {section === SECTION.NAME_TAG && <GroupProfilePanel onSaved={goBack} scopeNoun="conversation" scopeTitle={peerName} />}
          {section === SECTION.BACKGROUND && <DirectMessageBackgroundImageSettings />}
        </ModalContent>
      </ModalBody>
    </Modal>
  );
};

export default DirectMessagePreferencesModal;
